import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import Button from '../components/ui/Button';
import { SearchIcon, FilterIcon, BriefcaseIcon, MapPinIcon, GraduationCapIcon, UserIcon } from 'lucide-react';
const Careers = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const [activeFilter, setActiveFilter] = useState('all');
  const filters = [{
    id: 'all',
    label: 'All Positions'
  }, {
    id: 'research',
    label: 'Research'
  }, {
    id: 'data',
    label: 'Data & Analytics'
  }, {
    id: 'client',
    label: 'Client Services'
  }, {
    id: 'intern',
    label: 'Internships'
  }];
  const jobs = [{
    id: 1,
    title: 'Senior Research Analyst',
    department: 'research',
    location: 'Toronto, ON',
    type: 'Full-time',
    description: "Lead research projects focused on consumer behavior and market trends in Toronto's diverse communities.",
    requirements: ['5+ years of market research experience', 'Experience with both qualitative and quantitative methodologies', 'Strong analytical and communication skills', 'Experience working with diverse populations preferred']
  }, {
    id: 2,
    title: 'Data Scientist',
    department: 'data',
    location: 'Toronto, ON',
    type: 'Full-time',
    description: 'Apply advanced analytics and machine learning techniques to extract insights from complex datasets.',
    requirements: ["Master's degree in Statistics, Data Science, or related field", 'Experience with Python, R, and SQL', 'Familiarity with machine learning algorithms', 'Strong problem-solving abilities']
  }, {
    id: 3,
    title: 'Client Success Manager',
    department: 'client',
    location: 'Toronto, ON',
    type: 'Full-time',
    description: 'Serve as the primary liaison between clients and our research team, ensuring project success and client satisfaction.',
    requirements: ['3+ years of account management or client service experience', 'Strong communication and presentation skills', 'Understanding of market research processes', 'Experience in a consulting environment preferred']
  }, {
    id: 4,
    title: 'Research Intern',
    department: 'intern',
    location: 'Toronto, ON',
    type: 'Internship (4 months)',
    description: 'Support research projects through data collection, analysis, and reporting while gaining hands-on experience.',
    requirements: ["Currently enrolled in a Bachelor's or Master's program in Marketing, Business, Statistics, or related field", 'Strong analytical skills and attention to detail', 'Excellent written and verbal communication', 'Ability to work in a fast-paced environment']
  }, {
    id: 5,
    title: 'Qualitative Research Specialist',
    department: 'research',
    location: 'Toronto, ON',
    type: 'Full-time',
    description: "Design and conduct focus groups, in-depth interviews, and ethnographic research across Toronto's diverse communities.",
    requirements: ['3+ years of qualitative research experience', 'Experience moderating focus groups and interviews', 'Strong interpersonal and observation skills', 'Multilingual abilities highly valued']
  }, {
    id: 6,
    title: 'Data Analytics Intern',
    department: 'intern',
    location: 'Toronto, ON',
    type: 'Internship (4 months)',
    description: 'Support our data analytics team in processing and visualizing research data while developing technical skills.',
    requirements: ['Currently pursuing a degree in Statistics, Computer Science, or related field', 'Familiarity with data analysis tools and programming languages', 'Strong problem-solving abilities', 'Keen attention to detail']
  }];
  const filteredJobs = activeFilter === 'all' ? jobs : jobs.filter(job => job.department === activeFilter);
  return <div className="pt-20" data-id="element-198">
      {/* Hero Section */}
      <section className="bg-neutral-light py-20" data-id="element-199">
        <div className="container mx-auto px-4 md:px-6" data-id="element-200">
          <div className="max-w-3xl mx-auto text-center" data-id="element-201">
            <motion.h1 initial={{
            opacity: 0,
            y: -20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5
          }} className="text-4xl md:text-5xl font-display font-bold mb-6" data-id="element-202">
              Careers at{' '}
              <span className="text-toronto-blue" data-id="element-203">Market Research Toronto</span>
            </motion.h1>
            <motion.p initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 0.2,
            duration: 0.5
          }} className="text-xl text-neutral-dark/80" data-id="element-204">
              Join our team of research professionals and help organizations
              make data-driven decisions.
            </motion.p>
          </div>
        </div>
      </section>
      {/* Why Work With Us */}
      <section className="py-16 bg-white" data-id="element-205">
        <div className="container mx-auto px-4 md:px-6" data-id="element-206">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-207">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} data-id="element-208">
              <h2 className="text-3xl font-display font-bold mb-6" data-id="element-209">
                Why Work With Us
              </h2>
              <p className="text-lg text-neutral-dark/80 mb-8" data-id="element-210">
                At Market Research Toronto, we're passionate about uncovering
                insights that drive decisions. Join our diverse team and build
                your career in one of Canada's most dynamic research
                environments.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-211">
                {[{
                title: 'Professional Growth',
                description: 'Continuous learning opportunities and clear career advancement paths.'
              }, {
                title: 'Diverse Projects',
                description: 'Work across industries and research methodologies to expand your expertise.'
              }, {
                title: 'Inclusive Culture',
                description: 'A workplace that celebrates diversity and values different perspectives.'
              }, {
                title: 'Work-Life Balance',
                description: 'Flexible scheduling options and competitive time-off policies.'
              }].map((benefit, index) => <div key={index} className="bg-neutral-light/50 p-4 rounded-lg" data-id="element-212">
                    <h3 className="text-lg font-semibold mb-2" data-id="element-213">
                      {benefit.title}
                    </h3>
                    <p className="text-neutral-dark/80" data-id="element-214">
                      {benefit.description}
                    </p>
                  </div>)}
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} data-id="element-215">
              <img src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1170&q=80" alt="Team collaboration" className="rounded-lg shadow-lg w-full h-auto" data-id="element-216" />
            </motion.div>
          </div>
        </div>
      </section>
      {/* Diversity Statement */}
      <section className="py-16 bg-toronto-blue text-white" data-id="element-217">
        <div className="container mx-auto px-4 md:px-6" data-id="element-218">
          <div className="max-w-3xl mx-auto text-center" data-id="element-219">
            <h2 className="text-3xl font-display font-bold mb-6" data-id="element-220">
              Our Commitment to Diversity
            </h2>
            <p className="text-xl mb-6 leading-relaxed" data-id="element-221">
              Market Research Toronto is committed to building a team that
              reflects the diversity of the communities we serve. We believe
              that our work is strengthened by a variety of perspectives,
              backgrounds, and experiences.
            </p>
            <p className="text-lg opacity-90" data-id="element-222">
              We are an equal opportunity employer and encourage applications
              from all qualified individuals, including members of visible
              minorities, Indigenous peoples, persons with disabilities, and
              members of the LGBTQ2+ community.
            </p>
          </div>
        </div>
      </section>
      {/* Current Openings */}
      <section className="py-16 bg-white" data-id="element-223">
        <div className="container mx-auto px-4 md:px-6" data-id="element-224">
          <h2 className="text-3xl font-display font-bold mb-8 text-center" data-id="element-225">
            Current Openings
          </h2>
          {/* Search and Filter */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8" data-id="element-226">
            <div className="relative max-w-md w-full" data-id="element-227">
              <input type="text" placeholder="Search positions..." className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-toronto-blue/30" data-id="element-228" />
              <SearchIcon size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-dark/50" data-id="element-229" />
            </div>
            <div className="flex items-center overflow-x-auto pb-2 md:pb-0 gap-2" data-id="element-230">
              <FilterIcon size={18} className="text-neutral-dark/70 mr-1" data-id="element-231" />
              <span className="text-sm font-medium mr-2" data-id="element-232">Filter:</span>
              {filters.map(filter => <button key={filter.id} onClick={() => setActiveFilter(filter.id)} className={`px-3 py-1 text-sm rounded-full whitespace-nowrap ${activeFilter === filter.id ? 'bg-toronto-blue text-white' : 'bg-neutral-light text-neutral-dark hover:bg-neutral-light/70'}`} data-id="element-233">
                  {filter.label}
                </button>)}
            </div>
          </div>
          {/* Jobs List */}
          <div className="space-y-6" data-id="element-234">
            {filteredJobs.map((job, index) => <motion.div key={job.id} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1,
            duration: 0.5
          }} className="border rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow" data-id="element-235">
                <div className="p-6" data-id="element-236">
                  <div className="flex flex-wrap justify-between items-start gap-4 mb-4" data-id="element-237">
                    <div data-id="element-238">
                      <h3 className="text-xl font-semibold" data-id="element-239">{job.title}</h3>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-2" data-id="element-240">
                        <div className="flex items-center text-neutral-dark/70" data-id="element-241">
                          <BriefcaseIcon size={16} className="mr-1" data-id="element-242" />
                          <span data-id="element-243">{job.type}</span>
                        </div>
                        <div className="flex items-center text-neutral-dark/70" data-id="element-244">
                          <MapPinIcon size={16} className="mr-1" data-id="element-245" />
                          <span data-id="element-246">{job.location}</span>
                        </div>
                      </div>
                    </div>
                    <Button variant="primary" data-id="element-247">Apply Now</Button>
                  </div>
                  <p className="text-neutral-dark/80 mb-4" data-id="element-248">{job.description}</p>
                  <div data-id="element-249">
                    <h4 className="font-medium mb-2" data-id="element-250">Requirements:</h4>
                    <ul className="list-disc pl-5 space-y-1" data-id="element-251">
                      {job.requirements.map((req, i) => <li key={i} data-id="element-252">{req}</li>)}
                    </ul>
                  </div>
                </div>
              </motion.div>)}
            {filteredJobs.length === 0 && <div className="text-center py-12 bg-neutral-light/30 rounded-lg" data-id="element-253">
                <p className="text-lg text-neutral-dark/70" data-id="element-254">
                  No positions currently available in this category.
                </p>
                <button onClick={() => setActiveFilter('all')} className="mt-4 text-toronto-blue hover:underline" data-id="element-255">
                  View all positions
                </button>
              </div>}
          </div>
        </div>
      </section>
      {/* Internships */}
      <section className="py-16 bg-neutral-light/50" data-id="element-256">
        <div className="container mx-auto px-4 md:px-6" data-id="element-257">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-258">
            <div data-id="element-259">
              <h2 className="text-3xl font-display font-bold mb-6" data-id="element-260">
                Internships & Co-op Programs
              </h2>
              <p className="text-lg text-neutral-dark/80 mb-6" data-id="element-261">
                We offer internship and co-op opportunities for students and
                recent graduates looking to gain hands-on experience in market
                research.
              </p>
              <div className="mb-8" data-id="element-262">
                <h3 className="text-xl font-semibold mb-3" data-id="element-263">
                  Partner Universities
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-id="element-264">
                  {['University of Toronto', 'Ryerson University', 'York University', 'OCAD University', 'Humber College', 'Seneca College'].map((school, index) => <div key={index} className="flex items-center" data-id="element-265">
                      <GraduationCapIcon size={16} className="text-toronto-blue mr-2" data-id="element-266" />
                      <span data-id="element-267">{school}</span>
                    </div>)}
                </div>
              </div>
              <p className="text-neutral-dark/80 mb-6" data-id="element-268">
                Don't see a current opening that fits your skills? We're always
                interested in connecting with talented individuals. Send us your
                resume for future opportunities.
              </p>
              <Button variant="primary" data-id="element-269">Submit Your Resume</Button>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md" data-id="element-270">
              <h3 className="text-xl font-semibold mb-4" data-id="element-271">
                What Our Interns Say
              </h3>
              <div className="space-y-6" data-id="element-272">
                {[{
                quote: 'My internship at Market Research Toronto gave me hands-on experience with real research projects. The mentorship I received was invaluable for my professional development.',
                name: 'Sophia Chen',
                program: 'MBA Candidate, Rotman School of Management'
              }, {
                quote: 'The team at Market Research Toronto treated me as a valuable contributor from day one. I gained practical skills that complemented my academic knowledge.',
                name: 'Marcus Johnson',
                program: 'Statistics & Data Science, University of Toronto'
              }].map((testimonial, index) => <div key={index} className="border-l-4 border-toronto-blue pl-4" data-id="element-273">
                    <p className="italic mb-3" data-id="element-274">"{testimonial.quote}"</p>
                    <div className="flex items-center" data-id="element-275">
                      <div className="bg-toronto-blue/10 rounded-full w-8 h-8 flex items-center justify-center mr-3" data-id="element-276">
                        <UserIcon size={16} className="text-toronto-blue" data-id="element-277" />
                      </div>
                      <div data-id="element-278">
                        <p className="font-medium" data-id="element-279">{testimonial.name}</p>
                        <p className="text-sm text-neutral-dark/70" data-id="element-280">
                          {testimonial.program}
                        </p>
                      </div>
                    </div>
                  </div>)}
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* CTA Section */}
      <section className="py-16 bg-toronto-blue text-white" data-id="element-281">
        <div className="container mx-auto px-4 md:px-6" data-id="element-282">
          <div className="max-w-3xl mx-auto text-center" data-id="element-283">
            <h2 className="text-3xl font-display font-bold mb-6" data-id="element-284">
              Join Our Talent Community
            </h2>
            <p className="text-xl mb-8 opacity-90" data-id="element-285">
              Stay connected with Market Research Toronto and be the first to
              know about new career opportunities.
            </p>
            <div className="max-w-md mx-auto" data-id="element-286">
              <form className="flex" data-id="element-287">
                <input type="email" placeholder="Your email address" className="flex-grow px-4 py-3 rounded-l text-neutral-dark focus:outline-none" data-id="element-288" />
                <Button variant="secondary" className="rounded-l-none" data-id="element-289">
                  Subscribe
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>;
};
export default Careers;